import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Copy, AlertTriangle, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/rbac";
import { localName, localFullName } from "../lib/localName";

const ALL_DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DEFAULT_WEEKEND = [5, 6]; // Gulf default (Fri + Sat)
const PERIODS = [1, 2, 3, 4, 5, 6, 7];

// Common weekend presets — user can also pick "custom" and click day chips.
const WEEKEND_PRESETS = [
  { id: "gulf",      label_key: "weekend_gulf",      days: [5, 6] }, // Fri+Sat
  { id: "levant",    label_key: "weekend_levant",    days: [6, 0] }, // Sat+Sun
  { id: "single_fri", label_key: "weekend_single_fri", days: [5] },  // Fri only
];

// Default period time slots — used to auto-fill when picking a period.
const PERIOD_TIMES = {
  1: { start: "08:00", end: "08:45" },
  2: { start: "08:50", end: "09:35" },
  3: { start: "09:40", end: "10:25" },
  4: { start: "10:40", end: "11:25" },
  5: { start: "11:30", end: "12:15" },
  6: { start: "12:20", end: "13:05" },
  7: { start: "13:10", end: "13:55" },
};

const EMPTY_FORM = {
  day: 0, period: 1, subject_id: "", teacher_id: "",
  start_time: "08:00", end_time: "08:45",
  room: "", meeting_url: "", meeting_platform: "",
};

export default function TimetablePage() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const canEdit = can.editTimetable(user);

  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [slots, setSlots] = useState([]);
  const [conflicts, setConflicts] = useState([]); // [{teacher_id, day, period, count}]

  const [school, setSchool] = useState("");
  const [classId, setClassId] = useState("");
  const [weekendDays, setWeekendDays] = useState(DEFAULT_WEEKEND);
  const [showWeekend, setShowWeekend] = useState(false);

  // Parent/Student auto-context (no school/class pickers; auto-select from child)
  const [myChildren, setMyChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState("");
  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";
  const isPersonal = isParent || isStudent;

  // Dialog state
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // ---------- Data loading ----------
  useEffect(() => { api.get("/schools").then((r) => { const list = r.data || []; setSchools(list); setSchool((prev) => prev || (list[0]?.id || "")); }); }, []);

  // Parent: load children. Student: load own record. Auto-pick first.
  useEffect(() => {
    if (isParent) {
      api.get("/my/children").then((r) => {
        const kids = r.data || [];
        setMyChildren(kids);
        if (kids.length && !selectedChild) {
          const first = kids[0];
          setSelectedChild(first.id);
          setSchool(first.school_id);
          setClassId(first.class_id);
        }
      });
    } else if (isStudent) {
      api.get("/my/student-record").then((r) => {
        const s = r.data;
        if (s) {
          setMyChildren([s]);
          setSelectedChild(s.id);
          setSchool(s.school_id);
          setClassId(s.class_id);
        }
      });
    }
  }, [isParent, isStudent]);

  // When parent picks a different child, re-resolve school/class
  const pickChild = (childId) => {
    const c = myChildren.find((k) => k.id === childId);
    if (!c) return;
    setSelectedChild(childId);
    setSchool(c.school_id);
    setClassId(c.class_id);
  };

  useEffect(() => {
    if (!school) return;
    api.get(`/classes?school_id=${school}`).then((r) => { const list = r.data || []; setClasses(list); setClassId((prev) => prev || (list[0]?.id || "")); });
    api.get(`/subjects?school_id=${school}`).then((r) => setSubjects(r.data || []));
    api.get(`/teachers?school_id=${school}`).then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setTeachers(items);
    });
    // Pull this school's weekend pattern
    const obj = schools.find((s) => s.id === school);
    const wd = Array.isArray(obj?.weekend_days) && obj.weekend_days.length > 0
      ? obj.weekend_days
      : DEFAULT_WEEKEND;
    setWeekendDays(wd);
  }, [school, schools]);

  const saveWeekend = async (days) => {
    setWeekendDays(days);
    try {
      const obj = schools.find((s) => s.id === school);
      if (!obj) return;
      await api.put(`/schools/${school}`, { ...obj, weekend_days: days });
      // Refresh schools list to reflect updated value
      const r = await api.get("/schools");
      setSchools(r.data || []);
      toast.success(t("saved_successfully"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const toggleWeekendDay = (d) => {
    const next = weekendDays.includes(d)
      ? weekendDays.filter((x) => x !== d)
      : [...weekendDays, d].sort();
    if (next.length === 7) {
      toast.error(t("cannot_all_weekend"));
      return;
    }
    saveWeekend(next);
  };

  const reloadSlots = () => {
    if (classId) api.get(`/timetable?class_id=${classId}`).then((r) => setSlots(r.data || []));
  };
  const reloadConflicts = () => {
    if (school && canEdit) {
      api.get(`/timetable/conflicts?school_id=${school}`)
        .then((r) => setConflicts(r.data || []))
        .catch(() => setConflicts([]));
    }
  };

  useEffect(() => { reloadSlots(); }, [classId]);
  useEffect(() => { reloadConflicts(); }, [school, slots.length]);

  // Derived: work days = all days minus weekend; optionally append weekend.
  const workDays = [0, 1, 2, 3, 4, 5, 6].filter((d) => !weekendDays.includes(d));
  const visibleDays = showWeekend ? [...Array(7).keys()] : workDays;
  const matchedPreset = WEEKEND_PRESETS.find(
    (p) => p.days.length === weekendDays.length && p.days.every((d) => weekendDays.includes(d)),
  );
  const todayIdx = (() => {
    const d = new Date().getDay();
    return d;
  })();

  const grid = useMemo(() => {
    const g = {};
    slots.forEach((s) => { g[`${s.day}-${s.period}`] = s; });
    return g;
  }, [slots]);

  const conflictSet = useMemo(() => {
    // Keys like `${teacher_id}-${day}-${period}` for fast lookup.
    const set = new Set();
    conflicts.forEach((c) => set.add(`${c.teacher_id}-${c.day}-${c.period}`));
    return set;
  }, [conflicts]);

  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s])), [subjects]);
  const teacherById = useMemo(() => Object.fromEntries(teachers.map((tt) => [tt.id, tt])), [teachers]);

  // ---------- Open dialog flows ----------
  const openCreate = (day, period) => {
    if (!canEdit) return;
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      day,
      period,
      start_time: PERIOD_TIMES[period]?.start || "08:00",
      end_time: PERIOD_TIMES[period]?.end || "08:45",
    });
    setOpen(true);
  };

  const openEdit = (slot) => {
    if (!canEdit) return;
    setEditingId(slot.id);
    setForm({
      day: slot.day,
      period: slot.period,
      subject_id: slot.subject_id || "",
      teacher_id: slot.teacher_id || "",
      start_time: slot.start_time || "08:00",
      end_time: slot.end_time || "08:45",
      room: slot.room || "",
      meeting_url: slot.meeting_url || "",
      meeting_platform: slot.meeting_platform || "",
    });
    setOpen(true);
  };

  // Update times when day/period changes inside the dialog
  const setPeriod = (p) => {
    const period = parseInt(p);
    setForm((f) => ({
      ...f,
      period,
      start_time: PERIOD_TIMES[period]?.start || f.start_time,
      end_time: PERIOD_TIMES[period]?.end || f.end_time,
    }));
  };

  const save = async () => {
    if (!form.subject_id || !form.teacher_id) {
      toast.error(t("subject_and_teacher_required"));
      return;
    }
    try {
      const payload = {
        ...form,
        school_id: school,
        class_id: classId,
        day: parseInt(form.day),
        period: parseInt(form.period),
      };
      if (editingId) {
        await api.put(`/timetable/${editingId}`, payload);
        toast.success(t("updated_successfully"));
      } else {
        // Skip if cell is already taken
        if (grid[`${payload.day}-${payload.period}`]) {
          toast.error(t("slot_already_taken"));
          return;
        }
        await api.post("/timetable", payload);
        toast.success(t("saved_successfully"));
      }
      setOpen(false);
      setEditingId(null);
      reloadSlots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const del = async () => {
    if (!editingId) return;
    if (!window.confirm(t("delete_slot_confirm"))) return;
    try {
      await api.delete(`/timetable/${editingId}`);
      toast.success(t("deleted_successfully"));
      setOpen(false);
      setEditingId(null);
      reloadSlots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const duplicateToDay = async (slot, targetDay) => {
    if (slot.day === targetDay) return;
    if (grid[`${targetDay}-${slot.period}`]) {
      toast.error(t("slot_already_taken"));
      return;
    }
    try {
      const payload = {
        school_id: school, class_id: classId,
        day: targetDay, period: slot.period,
        subject_id: slot.subject_id, teacher_id: slot.teacher_id,
        start_time: slot.start_time, end_time: slot.end_time,
        room: slot.room || "",
        meeting_url: slot.meeting_url || "",
        meeting_platform: slot.meeting_platform || "",
      };
      await api.post("/timetable", payload);
      toast.success(t("copied_successfully"));
      reloadSlots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const clearWeek = async () => {
    if (!window.confirm(t("clear_week_confirm"))) return;
    try {
      await Promise.all(slots.map((s) => api.delete(`/timetable/${s.id}`)));
      toast.success(t("cleared"));
      reloadSlots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-primary" />
            {t("timetable")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("timetable_hint_interactive")}</p>
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          {isPersonal ? (
            myChildren.length > 1 ? (
              <Select value={selectedChild} onValueChange={pickChild}>
                <SelectTrigger className="w-56" data-testid="tt-child-select"><SelectValue placeholder={t("select_child")} /></SelectTrigger>
                <SelectContent>{myChildren.map((c) => <SelectItem key={c.id} value={c.id}>{localFullName(c, lang)}</SelectItem>)}</SelectContent>
              </Select>
            ) : myChildren.length === 1 ? (
              <span className="text-sm text-muted-foreground bg-muted/40 px-3 py-2 rounded-md">
                {myChildren[0].full_name_ar || myChildren[0].full_name}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">{t("no_linked_children")}</span>
            )
          ) : (
            <>
              <Select value={school} onValueChange={setSchool}>
                <SelectTrigger className="w-48" data-testid="tt-school-select"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
                <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger className="w-48" data-testid="tt-class-select"><SelectValue placeholder={t("select_class")} /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
          {canEdit && classId && (
            <Button variant="outline" size="sm" onClick={() => setShowWeekend(!showWeekend)} data-testid="tt-toggle-weekend">
              {showWeekend ? t("hide_weekend") : t("show_weekend")}
            </Button>
          )}
          {canEdit && classId && slots.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearWeek} data-testid="tt-clear-week" className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30">
              <Trash2 className="w-4 h-4 me-1" /> {t("clear_week")}
            </Button>
          )}
        </div>
      </div>

      {/* Weekend / work-week pattern picker (per-school setting) */}
      {school && canEdit && !isPersonal && (
        <Card className="p-3 border shadow-none" data-testid="weekend-picker">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground font-semibold flex items-center gap-1.5">
              <CalendarIcon className="w-4 h-4" />
              {t("weekend_pattern")}:
            </span>
            {WEEKEND_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => saveWeekend(p.days)}
                className={`px-3 py-1 rounded-full border text-xs transition
                  ${matchedPreset?.id === p.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:border-primary/40 hover:bg-muted/50"}`}
                data-testid={`weekend-preset-${p.id}`}
                title={p.days.map((d) => t(ALL_DAYS[d])).join(" + ")}
              >
                {t(p.label_key)}
              </button>
            ))}

            <span className="text-muted-foreground text-xs mx-1">|</span>
            <span className="text-xs text-muted-foreground">{t("or_pick_days")}:</span>
            <div className="flex flex-wrap gap-1">
              {ALL_DAYS.map((d, i) => (
                <button
                  key={d}
                  onClick={() => toggleWeekendDay(i)}
                  className={`w-9 h-9 rounded-md text-xs border transition flex items-center justify-center
                    ${weekendDays.includes(i)
                      ? "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800"
                      : "bg-muted/30 text-muted-foreground border-border hover:border-primary/40"}`}
                  data-testid={`weekend-day-${d}`}
                  title={weekendDays.includes(i) ? t("weekend_day") : t("work_day")}
                >
                  {t(d)}
                </button>
              ))}
            </div>
            <span className="text-xs text-muted-foreground ms-auto">
              {t("current_pattern")}: <strong className="text-foreground">
                {weekendDays.length === 0
                  ? t("no_weekend")
                  : weekendDays.map((d) => t(ALL_DAYS[d])).join(" + ")}
              </strong>
            </span>
          </div>
        </Card>
      )}

      {/* Legend */}
      {classId && (
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-primary/15 border border-primary/40" /> {t("legend_filled")}</span>
          {canEdit && <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border-2 border-dashed border-border" /> {t("legend_empty_click")}</span>}
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> {t("legend_today")}</span>
          {canEdit && conflicts.length > 0 && (
            <span className="flex items-center gap-1.5 text-rose-600">
              <AlertTriangle className="w-3.5 h-3.5" />
              {t("conflicts_found", { n: conflicts.length })}
            </span>
          )}
        </div>
      )}

      {classId ? (
        <Card className="p-3 sm:p-4 border shadow-none overflow-x-auto" data-testid="tt-grid">
          <div
            className="grid gap-2 min-w-[640px]"
            style={{ gridTemplateColumns: `64px repeat(${visibleDays.length}, minmax(140px, 1fr))` }}
          >
            {/* Header row */}
            <div />
            {visibleDays.map((di) => (
              <div
                key={di}
                className={`text-center text-xs font-semibold uppercase tracking-wider p-2 rounded-md
                  ${di === todayIdx ? "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200" : "text-muted-foreground"}`}
                data-testid={`tt-day-header-${ALL_DAYS[di]}`}
              >
                {t(ALL_DAYS[di])}
              </div>
            ))}

            {/* Period rows */}
            {PERIODS.map((p) => (
              <PeriodRow
                key={p}
                period={p}
                days={visibleDays}
                todayIdx={todayIdx}
                grid={grid}
                subjectById={subjectById}
                teacherById={teacherById}
                conflictSet={conflictSet}
                canEdit={canEdit}
                onEmptyClick={openCreate}
                onSlotClick={openEdit}
                onDuplicate={duplicateToDay}
                t={t}
                lang={lang}
              />
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-12 text-center text-muted-foreground border-dashed">
          {school ? t("select_class") : t("select_school_first")}
        </Card>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent data-testid="tt-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? t("edit_slot") : t("new_slot")}
              <span className="ms-2 text-sm font-normal text-muted-foreground">
                — {t(ALL_DAYS[form.day])} · {t("period")} {form.period}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("day")}</Label>
                <Select value={String(form.day)} onValueChange={(v) => setForm({ ...form, day: parseInt(v) })}>
                  <SelectTrigger data-testid="dlg-day"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {visibleDays.map((i) => <SelectItem key={i} value={String(i)}>{t(ALL_DAYS[i])}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("period")}</Label>
                <Select value={String(form.period)} onValueChange={setPeriod}>
                  <SelectTrigger data-testid="dlg-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERIODS.map((p) => <SelectItem key={p} value={String(p)}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>{t("select_subject")}</Label>
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="dlg-subject"><SelectValue placeholder={t("select_subject")} /></SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t("teacher")}</Label>
              <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                <SelectTrigger data-testid="dlg-teacher"><SelectValue placeholder={t("teacher")} /></SelectTrigger>
                <SelectContent>
                  {teachers.map((tt) => (
                    <SelectItem key={tt.id} value={tt.id}>{tt.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.teacher_id && conflictSet.has(`${form.teacher_id}-${form.day}-${form.period}`) && (
                <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {t("teacher_already_booked_warning")}
                </p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t("start")}</Label>
                <Input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="dlg-start" />
              </div>
              <div>
                <Label>{t("end")}</Label>
                <Input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="dlg-end" />
              </div>
              <div>
                <Label>{t("room")}</Label>
                <Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="dlg-room" />
              </div>
            </div>

            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground">{t("online_meeting_optional")}</summary>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="col-span-2">
                  <Label>Meeting URL</Label>
                  <Input value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} placeholder="https://zoom.us/..." data-testid="dlg-meeting-url" />
                </div>
                <div>
                  <Label>Platform</Label>
                  <Select value={form.meeting_platform || ""} onValueChange={(v) => setForm({ ...form, meeting_platform: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zoom">Zoom</SelectItem>
                      <SelectItem value="teams">Teams</SelectItem>
                      <SelectItem value="meet">Meet</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </details>
          </div>

          <DialogFooter className="gap-2">
            {editingId && (
              <Button variant="outline" onClick={del} className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 me-auto" data-testid="dlg-delete">
                <Trash2 className="w-4 h-4 me-1" /> {t("delete")}
              </Button>
            )}
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="dlg-cancel">{t("cancel")}</Button>
            <Button onClick={save} data-testid="dlg-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PeriodRow({ period, days, todayIdx, grid, subjectById, teacherById, conflictSet,
                    canEdit, onEmptyClick, onSlotClick, onDuplicate, t, lang }) {
  const time = PERIOD_TIMES[period];
  return (
    <>
      <div className="grid place-items-center p-2 rounded-md bg-muted/30">
        <div className="text-xs font-mono font-bold">{t("period_short", { n: period })}</div>
        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{time?.start}</div>
      </div>
      {days.map((di) => {
        const s = grid[`${di}-${period}`];
        const isToday = di === todayIdx;
        if (!s) {
          return (
            <button
              key={`${di}-${period}`}
              type="button"
              onClick={() => canEdit && onEmptyClick(di, period)}
              disabled={!canEdit}
              className={`min-h-[78px] rounded-md border-2 border-dashed p-2 text-xs transition
                ${canEdit ? "border-border hover:border-primary hover:bg-primary/5 cursor-pointer" : "border-border opacity-60 cursor-default"}
                ${isToday ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}
              `}
              data-testid={`tt-cell-empty-${di}-${period}`}
            >
              {canEdit && (
                <span className="text-muted-foreground/70 flex flex-col items-center justify-center h-full">
                  <Plus className="w-4 h-4" />
                  <span className="mt-0.5">{t("add")}</span>
                </span>
              )}
            </button>
          );
        }
        const sub = subjectById[s.subject_id];
        const tch = teacherById[s.teacher_id];
        const isConflict = conflictSet.has(`${s.teacher_id}-${s.day}-${s.period}`);
        return (
          <div
            key={`${di}-${period}`}
            className={`min-h-[78px] rounded-md border p-2 text-xs relative group transition
              ${isConflict ? "border-rose-400 bg-rose-50 dark:bg-rose-950/30" : "border-primary/30 bg-primary/5"}
              ${isToday ? "ring-1 ring-amber-300" : ""}
              ${canEdit ? "cursor-pointer hover:shadow-md hover:border-primary" : ""}
            `}
            onClick={() => canEdit && onSlotClick(s)}
            data-testid={`tt-cell-filled-${di}-${period}`}
          >
            <div className="flex items-start justify-between gap-1">
              <div className="font-semibold leading-tight truncate" title={localName(sub, lang)}>
                {localName(sub, lang) || "—"}
              </div>
              {isConflict && (
                <AlertTriangle
                  className="w-3.5 h-3.5 text-rose-600 shrink-0"
                  title={t("teacher_double_booked")}
                />
              )}
            </div>
            <div className="text-muted-foreground truncate mt-0.5" title={tch?.full_name}>
              {tch?.full_name || "—"}
            </div>
            <div className="text-muted-foreground font-mono text-[10px] mt-0.5">
              {s.start_time}–{s.end_time}
            </div>
            {s.room && <div className="text-muted-foreground text-[10px] truncate">📍 {s.room}</div>}
            {s.meeting_url && (
              <a
                href={s.meeting_url} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-block mt-0.5 text-primary underline text-[10px]"
                data-testid={`slot-join-${s.id}`}
              >
                {t("join_meeting")} {s.meeting_platform && `(${s.meeting_platform})`}
              </a>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // Duplicate to the NEXT visible day (round-robin)
                  const i = days.indexOf(di);
                  const next = days[(i + 1) % days.length];
                  onDuplicate(s, next);
                }}
                className="absolute top-1 end-1 opacity-0 group-hover:opacity-100 transition p-0.5 rounded hover:bg-primary/20"
                title={t("duplicate_next_day")}
                data-testid={`tt-duplicate-${s.id}`}
              >
                <Copy className="w-3 h-3 text-muted-foreground" />
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}
